import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Store, CreditCard, LogOut, Shield, Activity,
  ChevronRight, RefreshCw, AlertCircle, FileText, ClipboardList, Landmark, DollarSign, TrendingUp, UserX, Building, Gauge, AlertTriangle, MessageSquare, Bell, Download, Scale, Sparkles, BarChart3, CheckSquare
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../utils/apiConfig';

export default function AdminDashboardPortal() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('pecash_token');
  const isAdmin = localStorage.getItem('pecash_admin') === 'true';

  const fetchUserAndStats = useCallback(async () => {
    const token = getToken();
    
    if (!token || !isAdmin) {
      navigate('/admin');
      return;
    }

    try {
      // Verify user is admin
      const userResponse = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!userResponse.ok) {
        throw new Error('Session expired');
      }

      const userData = await userResponse.json();
      
      if (userData.user?.role !== 'admin') {
        throw new Error('Not authorized');
      }

      setUser(userData.user);

      // Log dashboard view
      await fetch(`${API_URL}/api/admin/auth/dashboard-view`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Fetch stats
      const statsResponse = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      // Clear invalid session
      localStorage.removeItem('pecash_token');
      localStorage.removeItem('pecash_admin');
      setTimeout(() => navigate('/admin'), 2000);
    }
  }, [navigate, isAdmin]);

  useEffect(() => {
    fetchUserAndStats();
  }, [fetchUserAndStats]);

  const handleLogout = async () => {
    const token = getToken();
    
    try {
      await fetch(`${API_URL}/api/admin/auth/logout`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (e) {
      // Continue with logout even if logging fails
    }

    localStorage.removeItem('pecash_token');
    localStorage.removeItem('pecash_admin');
    navigate('/admin');
  };
  
  const downloadComplianceDocument = async () => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/compliance/legal-review-document`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download document');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PeCashGlobal_Compliance_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Compliance document downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download compliance document');
    }
  };
  
  const triggerExpiryAlerts = async () => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/run-expiry-alerts?days_until_expiry=3`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to trigger alerts');
      }
      
      const result = await response.json();
      toast.success(`Sent ${result.results.alerts_sent} expiry alerts`);
    } catch (error) {
      console.error('Expiry alerts error:', error);
      toast.error('Failed to send expiry alerts');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center bg-slate-800 rounded-2xl p-8 border border-red-800">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-red-400 mb-4">{error}</p>
          <p className="text-slate-500 text-sm">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">PeCashGlobal Admin</h1>
              <p className="text-sm text-slate-400">{user?.email}</p>
            </div>
          </div>
          
          <Button 
            onClick={handleLogout}
            variant="ghost"
            className="text-slate-400 hover:text-white hover:bg-slate-700"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Security Notice */}
        <div className="bg-amber-900/20 border border-amber-800/50 rounded-xl p-4 mb-8">
          <p className="text-amber-300 text-sm flex items-center gap-2">
            <Activity className="w-4 h-4" />
            All admin actions are logged and audited. Session expires in 12 hours.
          </p>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-blue-900/50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-slate-400 text-sm">Total Users</span>
              </div>
              <p className="text-3xl font-bold text-white">{stats.total_users}</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-emerald-900/50 flex items-center justify-center">
                  <Store className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-slate-400 text-sm">Merchants</span>
              </div>
              <p className="text-3xl font-bold text-white">{stats.total_merchants}</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-purple-900/50 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-slate-400 text-sm">Transactions</span>
              </div>
              <p className="text-3xl font-bold text-white">{stats.total_transactions}</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-amber-900/50 flex items-center justify-center">
                  <span className="text-amber-400 font-bold">$</span>
                </div>
                <span className="text-slate-400 text-sm">Total Volume</span>
              </div>
              <p className="text-3xl font-bold text-white">${stats.total_volume?.toLocaleString() || 0}</p>
            </motion.div>
          </div>
        )}

        {/* Navigation Cards */}
        <h2 className="text-lg font-semibold text-white mb-4">Admin Sections</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            onClick={() => navigate('/admin/users')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-purple-600 transition-colors group"
            data-testid="admin-users-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-900/50 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Users & Merchants</h3>
                  <p className="text-slate-400 text-sm">Manage user accounts</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            onClick={() => navigate('/admin/transactions')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-purple-600 transition-colors group"
            data-testid="admin-transactions-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-900/50 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Transactions</h3>
                  <p className="text-slate-400 text-sm">View all transactions</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            onClick={() => navigate('/admin/authorizations')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-purple-600 transition-colors group"
            data-testid="admin-authorizations-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-900/50 flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Authorizations</h3>
                  <p className="text-slate-400 text-sm">Manage purchase authorizations</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            onClick={() => navigate('/admin/analytics')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-purple-600 transition-colors group"
            data-testid="admin-analytics-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-900/50 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Analytics</h3>
                  <p className="text-slate-400 text-sm">View platform metrics</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            onClick={() => navigate('/admin/audit')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-purple-600 transition-colors group"
            data-testid="admin-audit-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-900/50 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Audit Trail</h3>
                  <p className="text-slate-400 text-sm">Review admin actions</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            onClick={() => navigate('/admin/treasury')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-purple-600 transition-colors group"
            data-testid="admin-treasury-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-teal-900/50 flex items-center justify-center">
                  <Landmark className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Treasury & Reconciliation</h3>
                  <p className="text-slate-400 text-sm">Financial source of truth</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            onClick={() => navigate('/admin/pricing')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-purple-600 transition-colors group"
            data-testid="admin-pricing-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-orange-900/50 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Pricing Engine</h3>
                  <p className="text-slate-400 text-sm">Fee tiers & transaction limits</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05 }}
            onClick={() => navigate('/admin/pricing-simulator')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-purple-600 transition-colors group"
            data-testid="admin-pricing-simulator-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-violet-900/50 flex items-center justify-center">
                  <Gauge className="w-6 h-6 text-violet-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Pricing Simulator</h3>
                  <p className="text-slate-400 text-sm">Unit economics & contribution analysis</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.07 }}
            onClick={() => navigate('/admin/investor-demo')}
            className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-2xl p-6 cursor-pointer hover:border-purple-500 transition-colors group"
            data-testid="admin-investor-demo-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Investor Demo</h3>
                  <p className="text-slate-400 text-sm">5-minute guided walkthrough</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.08 }}
            onClick={() => navigate('/admin/merchant-applications')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-amber-600 transition-colors group"
            data-testid="admin-merchant-applications-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-900/50 flex items-center justify-center">
                  <CheckSquare className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Merchant Applications</h3>
                  <p className="text-slate-400 text-sm">Review & approve colmado registrations</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.09 }}
            onClick={() => navigate('/admin/demo-analytics')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-indigo-600 transition-colors group"
            data-testid="admin-demo-analytics-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-indigo-900/50 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Demo Analytics</h3>
                  <p className="text-slate-400 text-sm">Investor demo completion & engagement</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-indigo-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            onClick={() => navigate('/admin/fx')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-purple-600 transition-colors group"
            data-testid="admin-fx-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-cyan-900/50 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Exchange Rates</h3>
                  <p className="text-slate-400 text-sm">FX rate management</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            onClick={() => navigate('/admin/enforcement')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-red-600 transition-colors group"
            data-testid="admin-enforcement-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-900/50 flex items-center justify-center">
                  <UserX className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Risk & Enforcement</h3>
                  <p className="text-slate-400 text-sm">User suspension & fraud control</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-red-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
            onClick={() => navigate('/admin/settlements')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-emerald-600 transition-colors group"
            data-testid="admin-settlements-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-900/50 flex items-center justify-center">
                  <Building className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Merchant Settlements</h3>
                  <p className="text-slate-400 text-sm">Colmado payout batches</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
            onClick={() => navigate('/admin/limits')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-purple-600 transition-colors group"
            data-testid="admin-limits-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-900/50 flex items-center justify-center">
                  <Gauge className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Transaction Limits</h3>
                  <p className="text-slate-400 text-sm">Velocity & spending controls</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5 }}
            onClick={() => navigate('/admin/risk')}
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 cursor-pointer hover:border-red-600 transition-colors group"
            data-testid="admin-risk-section"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-900/50 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Risk Dashboard</h3>
                  <p className="text-slate-400 text-sm">Fraud indicators & alerts</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-red-400 transition-colors" />
            </div>
          </motion.div>

          {/* Support Cases */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            onClick={() => navigate('/admin/support')}
            data-testid="admin-nav-support"
            className="group cursor-pointer bg-slate-800 border border-slate-700 rounded-2xl p-5 hover:border-emerald-600/50 transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-900/50 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Support Cases</h3>
                  <p className="text-slate-400 text-sm">Customer & merchant issues</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400 transition-colors" />
            </div>
          </motion.div>

          {/* Merchant Onboarding */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            onClick={() => navigate('/admin/onboarding')}
            data-testid="admin-nav-onboarding"
            className="group cursor-pointer bg-slate-800 border border-slate-700 rounded-2xl p-5 hover:border-blue-600/50 transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-900/50 flex items-center justify-center">
                  <Building className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Merchant Onboarding</h3>
                  <p className="text-slate-400 text-sm">Application review & activation</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-blue-400 transition-colors" />
            </div>
          </motion.div>

          {/* System Health */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            onClick={() => navigate('/admin/health')}
            data-testid="admin-nav-health"
            className="group cursor-pointer bg-slate-800 border border-slate-700 rounded-2xl p-5 hover:border-cyan-600/50 transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-cyan-900/50 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">System Health</h3>
                  <p className="text-slate-400 text-sm">Monitoring & telemetry</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-cyan-400 transition-colors" />
            </div>
          </motion.div>

          {/* Notification Center */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            onClick={() => navigate('/admin/notifications')}
            data-testid="admin-nav-notifications"
            className="group cursor-pointer bg-slate-800 border border-slate-700 rounded-2xl p-5 hover:border-purple-600/50 transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-900/50 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Notifications</h3>
                  <p className="text-slate-400 text-sm">Multi-channel messaging</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400 transition-colors" />
            </div>
          </motion.div>
        </div>

        {/* Quick Info */}
        <div className="mt-8 bg-slate-800 border border-slate-700 rounded-2xl p-6">
          <h3 className="text-white font-semibold mb-4">Quick Stats</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-400">Supporters</p>
              <p className="text-white font-medium">{stats?.total_senders || 0}</p>
            </div>
            <div>
              <p className="text-slate-400">Pending Payouts</p>
              <p className="text-white font-medium">{stats?.pending_payouts || 0}</p>
            </div>
            <div>
              <p className="text-slate-400">Active Scheduled</p>
              <p className="text-white font-medium">{stats?.active_recurring_transfers || 0}</p>
            </div>
            <div>
              <p className="text-slate-400">Total Receipts</p>
              <p className="text-white font-medium">{stats?.total_receipts || 0}</p>
            </div>
          </div>
        </div>
        
        {/* Compliance & Admin Tools */}
        <div className="mt-8 bg-slate-800 border border-slate-700 rounded-2xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Scale className="w-5 h-5 text-purple-400" />
            Compliance & Admin Tools
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-900/50 rounded-xl p-4">
              <h4 className="text-slate-300 font-medium mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-emerald-400" />
                Legal Review Document
              </h4>
              <p className="text-slate-500 text-sm mb-3">
                Download the regulatory compliance summary PDF for legal counsel review.
              </p>
              <Button
                onClick={downloadComplianceDocument}
                variant="outline"
                size="sm"
                className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/20"
                data-testid="download-compliance-doc-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
            <div className="bg-slate-900/50 rounded-xl p-4">
              <h4 className="text-slate-300 font-medium mb-2 flex items-center gap-2">
                <Bell className="w-4 h-4 text-orange-400" />
                Authorization Expiry Alerts
              </h4>
              <p className="text-slate-500 text-sm mb-3">
                Send reminder emails to supporters with authorizations expiring in 3 days.
              </p>
              <Button
                onClick={triggerExpiryAlerts}
                variant="outline"
                size="sm"
                className="border-orange-600 text-orange-400 hover:bg-orange-600/20"
                data-testid="trigger-expiry-alerts-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                Send Alerts Now
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
